import moment from "moment-mini";
import { capitalize } from "lodash";
import formatPrice from "./formatPrice";
import TV_CHART_MARKER_COLORS from "./tvChartMarkerColors";
import { getBreakoutBreakdownMarkerSpec, BREAKOUT_BREAKDOWN_MARKER_POSITIONS } from "./breakoutBreakdownMarkerConfig";
import { formatFinancialReportMarkerLabel } from "./reportDateConfirmationStatus";

export const markerTypesNames = {
  currentLastMinMaxPoints: "currentLastMinMaxPoints",
  previousLastMinMaxPoints: "previousLastMinMaxPoints",
  currentBreakoutBreakdown: "currentBreakoutBreakdown",
  previousBreakoutBreakdown: "previousBreakoutBreakdown",
  dividends: "dividends",
  financialReports: "financialReports",
  mediaMonitorMessages: "mediaMonitorMessages",
  aggregatedMediaMonitorMessages: "aggregatedMediaMonitorMessages",
  insiderTransactions: "insiderTransactions"
};

export const highsAndLowsTypes = {
  LCH: {
    arrowType: "arrow-down",
    disabledArrowType: "arrow-down-disabled",
    label: "OPS"
  },
  LCL: {
    arrowType: "arrow-up",
    disabledArrowType: "arrow-up-disabled",
    label: "OPD"
  }
};

export const aggregationMarkers = {
  aggregatedMediaMonitorMessages: {
    name: markerTypesNames.aggregatedMediaMonitorMessages,
    shouldDrawSettingName: "show_media_monitor_messages",
    markerColor: TV_CHART_MARKER_COLORS.info,
    markerLabel: "K",
    getMarkerTime: message => message.pub_datetime,
    getHtmlLabelInline: (message, translate) => {
      const displayedAmount = 3;
      const aggregatedMarkers = message?.aggregatedMarkers ?? [];

      let label = "";
      let i = 0;

      for (const marker of aggregatedMarkers) {
        if (i++ >= displayedAmount) {
          const remainingAmount =
            aggregatedMarkers.length - displayedAmount ?? 0;
          label += `... (+${remainingAmount})<br><br>`;
          break;
        }

        label += `${moment(marker.pub_datetime).format("DD.MM.YYYY, HH:mm")}${
          marker.source ? " - " + marker.source : ""
        }<br>${marker.title}<br><br>`;
      }

      label += `${translate("Click on a marker to learn more")}`;

      return label;
    },
    getMarkerMetaData: message => message.aggregatedMarkers
  }
};

const isBreakoutBreakdownPoint = point =>
  Boolean(getBreakoutBreakdownMarkerSpec(point?.trend_status));

const lastMinMaxPoints = {
  name: markerTypesNames.currentLastMinMaxPoints,
  markersDataKey: markerTypesNames.currentLastMinMaxPoints,
  shouldDrawSettingName: "show_current_last-min-max-points",
  shouldDrawMarker: point => Boolean(highsAndLowsTypes[point?.type]),
  shape: ({ type }, isDisabled = false) => {
    const { arrowType, disabledArrowType } = highsAndLowsTypes[type];
    return isDisabled ? disabledArrowType : arrowType;
  },
  getMarkerTime: report => report.datetime_utc,
  getLabel: (points, translate) =>
    translate(`%{type}: %{price}`, {
      price: Number(points.value).toFixed(2),
      type: highsAndLowsTypes[points.type].label
    }),
  getMarkerMetaData: () => {}
};

const breakoutBreakdown = {
  name: markerTypesNames.currentBreakoutBreakdown,
  markersDataKey: markerTypesNames.currentLastMinMaxPoints,
  shouldDrawSettingName: "show_current_last-min-max-points",
  shouldDrawMarker: point => isBreakoutBreakdownPoint(point),
  shape: point => getBreakoutBreakdownMarkerSpec(point.trend_status)?.shape,
  getShapeColor: point =>
    getBreakoutBreakdownMarkerSpec(point.trend_status)?.color,
  getMarkerPrice: point => {
    const price = Number(point?.confirmation_position);

    return Number.isFinite(price) ? price : undefined;
  },
  getMarkerPriceOffsetDirection: point => {
    const position = getBreakoutBreakdownMarkerSpec(point.trend_status)?.position;

    if (position === BREAKOUT_BREAKDOWN_MARKER_POSITIONS.ABOVE_CANDLE) {
      return 1;
    }

    if (position === BREAKOUT_BREAKDOWN_MARKER_POSITIONS.BELOW_CANDLE) {
      return -1;
    }

    return undefined;
  },
  getMarkerTime: point =>
    point.confirmation_datetime_utc ??
    point.confirmation_date_utc ??
    point.confirmation_date ??
    point.datetime_utc,
  hoverTooltip: {
    getText: (point, translate) => {
      const spec = getBreakoutBreakdownMarkerSpec(point.trend_status);

      if (!spec) {
        return "";
      }

      return translate(spec.tooltipTranslationKey);
    },
    hitRadiusPx: 28,
    horizontalBars: 2.5
  },
  hideVisibleLabel: true,
  getMarkerMetaData: () => {}
};

export const resolveMarkerHoverTooltip = (markerType, marker, translate) => {
  const hoverTooltip = markerType?.hoverTooltip;

  if (!hoverTooltip || typeof hoverTooltip.getText !== "function") {
    return null;
  }

  const text = hoverTooltip.getText(marker, translate);

  if (!text) {
    return null;
  }

  return {
    text,
    hitRadiusPx: hoverTooltip.hitRadiusPx,
    horizontalBars: hoverTooltip.horizontalBars
  };
};

export const markerTypes = [
  {
    name: markerTypesNames.dividends,
    shouldDrawSettingName: "show_dividends",
    markerColor: TV_CHART_MARKER_COLORS.warning,
    markerLabel: "D",
    getMarkerTime: dividend => dividend.last_day_with_right_to_dividend,
    //prettier-ignore
    getLabel: (dividend, translate) => dividend.has_multiple_payouts ?
      `${translate("Last trading day with right to dividend in year %{dividend_year}", { dividend_year: dividend.year, })}
${translate("Last day with right to dividend: %{last_day_with_right_to_dividend}", {last_day_with_right_to_dividend: moment(dividend.last_day_with_right_to_dividend).format("DD.MM.YYYY")})}
${translate("Dividend day: %{dividend_day}", {dividend_day: moment(dividend.dividend_day).format("DD.MM.YYYY")})}
${translate("Payout days:")}
${dividend.payouts.map(payout => `- ${moment(payout.day).format("DD.MM.YYYY")}: ${formatPrice({ value: payout.payout, currency: dividend.currency })}`).join(`
`)}
${translate("The next business day, the dividend will be cut off from the exchange rate.")}` :
      `${translate("Last trading day with right to dividend in year %{dividend_year}", { dividend_year: dividend.year, })}
${translate("Last day with right to dividend: %{last_day_with_right_to_dividend}", {last_day_with_right_to_dividend: moment(dividend.last_day_with_right_to_dividend).format("DD.MM.YYYY")})}
${translate("Dividend day: %{dividend_day}", {dividend_day: moment(dividend.dividend_day).format("DD.MM.YYYY")})}
${translate("Amount: %{price}", {price: formatPrice({ value: dividend.payout, currency: dividend.currency }),})}
${translate("Dividend payout day: %{payout_day}", {payout_day: moment(dividend.payout_day).format("DD.MM.YYYY")})}
${translate("The next business day, the dividend will be cut off from the exchange rate.")}
`,
    // prettier-ignore
    getHtmlLabelInline: (dividend, translate) => dividend.has_multiple_payouts ? `${translate("Last trading day with right to dividend in year %{dividend_year}<br>", { dividend_year: dividend.year, })}${translate("Last day with right to dividend: %{last_day_with_right_to_dividend}<br>", {last_day_with_right_to_dividend: moment(dividend.last_day_with_right_to_dividend).format("DD.MM.YYYY")})}${translate("Dividend day: %{dividend_day}<br>", {dividend_day: moment(dividend.dividend_day).format("DD.MM.YYYY")})}${translate("Payout days:<br>")}${dividend.payouts.map(payout => `- ${moment(payout.day).format("DD.MM.YYYY")}: ${formatPrice({ value: payout.payout, currency: dividend.currency })}`).join("<br>")}<br>${translate("The next business day, the dividend will be cut off from the exchange rate.")}` : `${translate("Last trading day with right to dividend in year %{dividend_year}<br>", { dividend_year: dividend.year, })}${translate("Last day with right to dividend: %{last_day_with_right_to_dividend}<br>", {last_day_with_right_to_dividend: moment(dividend.last_day_with_right_to_dividend).format("DD.MM.YYYY")})}${translate("Dividend day: %{dividend_day}<br>", {dividend_day: moment(dividend.dividend_day).format("DD.MM.YYYY")})}${translate("Amount: %{price}<br>", {price: formatPrice({ value: dividend.payout, currency: dividend.currency }),})}${translate("Dividend payout day: %{payout_day}<br>", {payout_day: moment(dividend.payout_day).format("DD.MM.YYYY")})}${translate("The next business day, the dividend will be cut off from the exchange rate.")}`,
    getMarkerMetaData: () => {}
  },
  {
    name: markerTypesNames.financialReports,
    shouldDrawSettingName: "show_financial_reports_dates",
    markerColor: TV_CHART_MARKER_COLORS.errorText,
    markerLabel: "R",
    getMarkerTime: report => report.report_date,
    getLabel: (report, translate) =>
      formatFinancialReportMarkerLabel(report, translate),
    getMarkerMetaData: () => {}
  },
  lastMinMaxPoints,
  {
    ...lastMinMaxPoints,
    shape: point => lastMinMaxPoints.shape(point, true),
    name: markerTypesNames.previousLastMinMaxPoints,
    markersDataKey: markerTypesNames.previousLastMinMaxPoints,
    shouldDrawSettingName: "show_previous_last-min-max-points"
  },
  breakoutBreakdown,
  {
    ...breakoutBreakdown,
    name: markerTypesNames.previousBreakoutBreakdown,
    markersDataKey: markerTypesNames.previousLastMinMaxPoints,
    shouldDrawSettingName: "show_previous_last-min-max-points"
  },
  {
    name: markerTypesNames.mediaMonitorMessages,
    shouldDrawSettingName: "show_media_monitor_messages",
    markerColor: TV_CHART_MARKER_COLORS.info,
    markerLabel: "K",
    getMarkerTime: message => message.pub_datetime,
    getLabel: (message, translate) =>
      `${moment(message.pub_datetime).format("DD.MM.YYYY, HH:mm")}${
        message.source ? " - " + message.source : ""
      }
${message.title}

${translate("Click on a marker to learn more")}`,
    // prettier-ignore
    getHtmlLabelInline: (message, translate) => `${moment(message.pub_datetime).format("DD.MM.YYYY, HH:mm")}${message.source ? " - " + message.source : ""}<br>${message.title}<br><br>${translate("Click on a marker to learn more")}`,
    getMarkerMetaData: message => message
  },
  {
    name: markerTypesNames.insiderTransactions,
    shouldDrawSettingName: "show_insider_transactions",
    markerColor: transaction => {
      switch (transaction.transaction_type) {
        case 0:
          return TV_CHART_MARKER_COLORS.error;
        case 1:
          return TV_CHART_MARKER_COLORS.success;
        default:
          return TV_CHART_MARKER_COLORS.baseGreyDark3;
      }
    },
    markerLabel: "I",
    getMarkerTime: transaction => transaction.date_from,
    // prettier-ignore
    getHtmlLabelInline: (transaction, translate) => `${transaction.date_from && transaction.date_to && transaction.date_from !== transaction.date_to ? translate("From") : translate("On day")}: ${moment(transaction.date_from).format("DD.MM.YYYY")}<br>${transaction.date_to && transaction.date_from !== transaction.date_to ? `${translate("To")}: ${moment(transaction.date_to).format("DD.MM.YYYY")}<br>` : ""}${transaction.insider_name ? `${transaction.insider_name} -` : ""} ${transaction.function_name ? transaction.function_name : ""}<br>${capitalize(translate(`insider_transaction_type_${transaction.transaction_type}`))} - ${transaction.shares_number} - ${transaction.price_avg || transaction.price_min}<br><a href="${window.location.href.split("#"[0])}#insiders-transactions" target="_top">${translate("Details")}</a>
`,
    getMarkerMetaData: transaction => transaction
  }
];
